/**
 * IPv4 search helpers shared by the top-nav search box and the events
 * filter sidebar. Input is validated to digits + dots only, at most four
 * octets (each 0–255, no leading zeros). The backend is the source of
 * truth for prefix-vs-exact semantics; these helpers are used for UI
 * state and chip labeling only.
 */

const IP_CHAR_RE = /^[0-9.]+$/;
// 15 chars is the max IPv4 length ("255.255.255.255").
const IP_MAX_LEN = 15;

function isValidOctet(s: string): boolean {
  if (!s || s.length > 3) return false;
  // Reject leading zeros so "01.02.03.04" doesn't silently miss canonical "1.2.3.4".
  if (s.length > 1 && s[0] === "0") return false;
  const n = Number(s);
  return Number.isInteger(n) && n >= 0 && n <= 255;
}

export function normalizeIpSearch(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > IP_MAX_LEN || !IP_CHAR_RE.test(trimmed)) return null;
  const hasTrailingDot = trimmed.endsWith(".");
  const stripped = hasTrailingDot ? trimmed.slice(0, -1) : trimmed;
  const octets = stripped.split(".");
  if (octets.length > 4) return null;
  if (!octets.every(isValidOctet)) return null;
  // Normalize "1.2.3.4." → "1.2.3.4" (full-IP user intent is exact match).
  // Keep a trailing dot when octets < 4 so isPrefixMode() and the sidebar
  // input round-trip the user's literal value.
  return hasTrailingDot && octets.length === 4 ? stripped : trimmed;
}

export function isPrefixMode(value: string): boolean {
  if (!value) return false;
  if (value.endsWith(".")) return true;
  return value.split(".").length < 4;
}
